<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useFilters, useOrderPayments } from "@/composables";
import type { OrderStatus } from "@/constants/order";
import { getEntityKey, isSameEntity } from "@/utils/sameEntity";
import OrderPaymentItem from "@/components/order/OrderPayment.vue";
import AddOrderPaymentButton from "@/components/buttons/AddOrderPaymentButton.vue";
import type { OrderPayment } from "@/api/orders/dto/order.dto";

const payments = defineModel<OrderPayment[]>({ default: () => [] });

const props = defineProps<{
  orderStatus?: OrderStatus;
}>();

const { t } = useI18n();
const { price } = useFilters();
const { createOrderPaymentDraft, getMandatoryOrderPaymentType } = useOrderPayments();

const activePayments = computed(() => payments.value.filter((payment) => payment.isActive !== false));

const sortedActivePayments = computed(() => {
  const mandatoryType = getMandatoryOrderPaymentType(props.orderStatus);

  return [...activePayments.value].sort((a, b) => Number(b.type === mandatoryType) - Number(a.type === mandatoryType));
});

const totalRate = computed(() => {
  return activePayments.value.reduce((total, payment) => (payment.isDeleted ? total : total + (payment.rate ?? 0)), 0);
});

const formattedTotalRate = computed(() => {
  const formatted = price(String(Math.abs(totalRate.value)));

  return totalRate.value < 0 ? `-${formatted}` : formatted;
});

function updatePayment(target: OrderPayment, data: Partial<OrderPayment>) {
  payments.value = payments.value.map((payment) => {
    if (isSameEntity(payment, target)) {
      return {
        ...payment,
        ...data,
      };
    }

    return payment;
  });
}

function addPayment() {
  payments.value = [...payments.value, createOrderPaymentDraft()];
}

function deletePayment(target: OrderPayment) {
  if (target.id) {
    updatePayment(target, { isDeleted: true });
    return;
  }

  payments.value = payments.value.filter((payment) => !isSameEntity(payment, target));
}
</script>

<template>
  <div class="order-payments pa-4 rounded-lg">
    <div class="text-grey-darken-1 font-weight-500 order-payments__title mb-5">{{ t("general.payments") }}</div>

    <div class="d-flex flex-column row-gap-4">
      <order-payment-item
        v-for="payment of sortedActivePayments"
        :key="getEntityKey(payment)"
        :model-value="payment"
        :order-status="props.orderStatus"
        @delete="deletePayment(payment)"
        @update:model-value="updatePayment(payment, $event)"
      />
    </div>

    <div class="d-flex align-center justify-space-between mt-4">
      <add-order-payment-button :order-status="props.orderStatus" @click="addPayment" />

      <div class="d-flex align-center col-gap-3 pt-1 font-weight-600 text-grey-darken-3 order-payments__total">
        <span>{{ t("payments.total-invoice-rate") }}:</span>
        <span data-qa="order-payments-total">{{ formattedTotalRate }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.order-payments {
  border: 1px solid $grey-light;

  &__title,
  &__total {
    font-size: 13px;
    line-height: 18px;
  }
}
</style>
